import React, { useState, useEffect } from 'react';
import { useCustomDialog } from '../context/DialogContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import '../styles/superadmin.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const BASE = 'https://menu.searchisend.in';

const SponsorPortal = () => {
    const { alert: customAlert } = useCustomDialog();
    const [token, setToken] = useState(localStorage.getItem('sponsor_token'));
    // eslint-disable-next-line no-unused-vars
    const [sponsor, setSponsor] = useState(JSON.parse(localStorage.getItem('sponsor_user')));
    
    const [activeTab, setActiveTab] = useState('overview');
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
    
    // Auth State
    const [isLogin, setIsLogin] = useState(true);
    const [authData, setAuthData] = useState({
        company_name: '', email: '', password: '',
        // KYC fields for registration
        contact_name: '', phone: '', business_phone: '',
        gst_number: '', pan_number: '', company_address: '', business_type: ''
    });
    const [authLoading, setAuthLoading] = useState(false);

    // Profile & Dashboard State
    const [profile, setProfile] = useState({});
    const [walletBalance, setWalletBalance] = useState(0);
    const [ads, setAds] = useState([]);
    
    // KYC State
    const [kycForm, setKycForm] = useState({ 
        contact_name: '', company_name: '', phone: '', business_phone: '',
        gst_number: '', pan_number: '', company_address: '', business_type: '' 
    });
    const [kycLoading, setKycLoading] = useState(false);

    // Transactions
    const [transactions, setTransactions] = useState([]);
    const [transLoading, setTransLoading] = useState(false);

    // Analytics
    const [analyticsAd, setAnalyticsAd] = useState('');
    const [analyticsData, setAnalyticsData] = useState([]);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsFilter, setAnalyticsFilter] = useState('7'); // '7', '30', 'all', 'custom'
    const [analyticsCustomDates, setAnalyticsCustomDates] = useState({ start: '', end: '' });

    // Create Ad State
    const [showCreateAd, setShowCreateAd] = useState(false);
    const [selectedAdForDetails, setSelectedAdForDetails] = useState(null);
    const [categories, setCategories] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [adForm, setAdForm] = useState({
        title: '', target_url: '', main_category_id: '', sub_category_id: '',
        target_state_ids: [], target_city_ids: [], target_pincode: '', placement_zone: 'in_menu_first', image: null
    });

    useEffect(() => {
        let interval;
        if (token) {
            fetchDashboard();
            fetchDropdownData();
            
            // Real-time polling every 10 seconds
            interval = setInterval(() => {
                fetchDashboard(true);
                if (activeTab === 'wallet') fetchTransactions(true);
                if (activeTab === 'analytics' && analyticsAd) fetchAnalytics(analyticsAd, true);
            }, 10000);
        }
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [token, activeTab, analyticsAd]);

    useEffect(() => {
        if (activeTab === 'wallet') fetchTransactions();
        if (activeTab === 'analytics' && ads.length > 0 && !analyticsAd) {
            setAnalyticsAd(ads[0].id);
        }
        // eslint-disable-next-line
    }, [activeTab]);

    useEffect(() => {
        if (analyticsAd) fetchAnalytics(analyticsAd);
        // eslint-disable-next-line
    }, [analyticsAd]);

    useEffect(() => {
        const fetchCities = async () => {
            if (!adForm.target_state_ids || adForm.target_state_ids.length === 0) {
                setCities([]);
                return;
            }
            try {
                const res = await fetch(`${BASE}/apimenu/get_locations.php?action=get_cities&state_ids=${adForm.target_state_ids.join(',')}`);
                const data = await res.json();
                if (data.success) setCities(data.cities);
            } catch (err) {}
        };
        fetchCities();
    }, [adForm.target_state_ids]);

    const fetchDropdownData = async () => {
        try {
            const cRes = await fetch(`${BASE}/apimenu/sponsor_api.php?action=get_categories`);
            const cData = await cRes.json();
            if (cData.success) setCategories(cData.categories);

            const sRes = await fetch(`${BASE}/apimenu/get_locations.php?action=get_states`);
            const sData = await sRes.json();
            if (sData.success) setStates(sData.states);
        } catch (err) {}
    };

    const fetchDashboard = async (silent = false) => {
        try {
            const fd = new FormData();
            fd.append('action', 'get_dashboard');
            const res = await fetch(`${BASE}/apimenu/sponsor_api.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setWalletBalance(data.wallet_balance);
                setAds(data.ads);
                setProfile(data.profile || {});
                if (!silent) {
                    setKycForm({
                        contact_name: data.profile.contact_name || '',
                        company_name: data.profile.company_name || '',
                        phone: data.profile.phone || '',
                        business_phone: data.profile.business_phone || '',
                        gst_number: data.profile.gst_number || '',
                        pan_number: data.profile.pan_number || '',
                        company_address: data.profile.company_address || '',
                        business_type: data.profile.business_type || ''
                    });
                }
            } else if (data.message === 'Unauthorized') {
                handleLogout();
            }
        } catch (err) { }
    };

    const fetchTransactions = async (silent = false) => {
        if (!silent) setTransLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'get_transactions');
            const res = await fetch(`${BASE}/apimenu/sponsor_api.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) setTransactions(data.transactions);
            else if (data.message === 'Unauthorized') handleLogout();
        } catch (err) {}
        finally { if (!silent) setTransLoading(false); }
    };

    const fetchAnalytics = async (adId, silent = false) => {
        if (!silent) setAnalyticsLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'get_ad_analytics');
            fd.append('ad_id', adId);
            const res = await fetch(`${BASE}/apimenu/sponsor_api.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                // Group by date
                const grouped = {};
                data.analytics.forEach(item => {
                    if (!grouped[item.date]) grouped[item.date] = { date: item.date, impressions: 0, clicks: 0 };
                    if (item.event_type === 'impression') grouped[item.date].impressions = item.count;
                    if (item.event_type === 'click') grouped[item.date].clicks = item.count;
                });
                setAnalyticsData(Object.values(grouped));
            } else if (data.message === 'Unauthorized') {
                handleLogout();
            }
        } catch(err) {}
        finally { if (!silent) setAnalyticsLoading(false); }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        const action = isLogin ? 'login' : 'register';
        try {
            const fd = new FormData();
            fd.append('action', action);
            fd.append('email', authData.email);
            fd.append('password', authData.password);
            if (!isLogin) {
                fd.append('company_name', authData.company_name);
                // Also send KYC fields if filled
                if (authData.contact_name)   fd.append('contact_name', authData.contact_name);
                if (authData.phone)          fd.append('phone', authData.phone);
                if (authData.business_phone) fd.append('business_phone', authData.business_phone);
                if (authData.gst_number)     fd.append('gst_number', authData.gst_number);
                if (authData.pan_number)     fd.append('pan_number', authData.pan_number);
                if (authData.company_address) fd.append('company_address', authData.company_address);
                if (authData.business_type)  fd.append('business_type', authData.business_type);
            }

            const res = await fetch(`${BASE}/apimenu/sponsor_api.php`, {
                method: 'POST',
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                if (isLogin) {
                    localStorage.setItem('sponsor_token', data.token);
                    localStorage.setItem('sponsor_user', JSON.stringify(data.sponsor));
                    setToken(data.token);
                    setSponsor(data.sponsor);
                } else {
                    await customAlert(data.message || 'Registration successful. Please check your email to verify your account.', 'Success', 'success');
                    setIsLogin(true);
                }
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('sponsor_token');
        localStorage.removeItem('sponsor_user');
        setToken(null);
        setSponsor(null);
    };

    const handleKycSubmit = async (e) => {
        e.preventDefault();
        setKycLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'update_kyc');
            Object.keys(kycForm).forEach(k => fd.append(k, kycForm[k]));
            
            const res = await fetch(`${BASE}/apimenu/sponsor_api.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('KYC details submitted for review', 'Success', 'success');
                fetchDashboard();
            } else if (data.message === 'Unauthorized') {
                handleLogout();
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch(err) {}
        finally { setKycLoading(false); }
    };

    const handleCreateAd = async (e) => {
        e.preventDefault();
        if (profile.kyc_status !== 'Verified') {
            await customAlert('You must complete KYC Verification and get approved before creating ads.', 'KYC Required', 'warning');
            return;
        }

        try {
            const fd = new FormData();
            fd.append('action', 'create_ad');
            Object.keys(adForm).forEach(key => {
                if (key === 'target_state_ids' || key === 'target_city_ids') {
                    fd.append(key, JSON.stringify(adForm[key]));
                } else if (adForm[key]) {
                    fd.append(key, adForm[key]);
                }
            });

            const res = await fetch(`${BASE}/apimenu/sponsor_api.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setShowCreateAd(false);
                fetchDashboard();
                await customAlert('Campaign created! Awaiting admin approval.', 'Success', 'success');
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) { }
    };

    if (!token) {
        const inputStyle = styles.input;
        const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px', display: 'block' };
        return (
            <div style={{...styles.authContainer, overflowY: 'auto', padding: '20px 0'}}>
                <div style={{...styles.glassCard, maxWidth: isLogin ? '420px' : '560px', margin: 'auto'}}>
                    <div style={styles.logoContainer}>
                        <div style={styles.logoIcon}>S</div>
                        <h2 style={{margin: 0, color: '#1e293b', fontSize: '1.5rem', fontWeight: 700}}>Sponsor Portal</h2>
                        <p style={{margin: '5px 0 20px', color: '#64748b', fontSize: '0.9rem'}}>Enterprise Advertising Network</p>
                    </div>

                    <form onSubmit={handleAuth} style={{display: 'flex', flexDirection: 'column', gap: '14px'}}>
                        {/* ── Account Details ── */}
                        {!isLogin && (
                            <>
                                <div style={{background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px'}}>
                                    <p style={{margin: '0 0 12px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem'}}>Account Details</p>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                                        <div><label style={labelStyle}>Company Name *</label>
                                            <input style={inputStyle} type="text" required placeholder="e.g. Acme Pvt Ltd" value={authData.company_name} onChange={e => setAuthData({...authData, company_name: e.target.value})} />
                                        </div>
                                    </div>
                                </div>

                                {/* ── KYC Details ── */}
                                <div style={{background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px'}}>
                                    <p style={{margin: '0 0 12px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem'}}>
                                        Business Verification <span style={{fontSize:'0.75rem', color:'#64748b', fontWeight: 400}}>(Optional — fill now for faster KYC approval)</span>
                                    </p>
                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                                        <div><label style={labelStyle}>Contact Person Name</label>
                                            <input style={inputStyle} type="text" placeholder="Full name" value={authData.contact_name} onChange={e => setAuthData({...authData, contact_name: e.target.value})} />
                                        </div>
                                        <div><label style={labelStyle}>Personal Phone</label>
                                            <input style={inputStyle} type="tel" placeholder="+91 XXXXXXXXXX" value={authData.phone} onChange={e => setAuthData({...authData, phone: e.target.value})} />
                                        </div>
                                        <div><label style={labelStyle}>Business Phone</label>
                                            <input style={inputStyle} type="tel" placeholder="Office number" value={authData.business_phone} onChange={e => setAuthData({...authData, business_phone: e.target.value})} />
                                        </div>
                                        <div><label style={labelStyle}>Business Type</label>
                                            <select style={inputStyle} value={authData.business_type} onChange={e => setAuthData({...authData, business_type: e.target.value})}>
                                                <option value="">Select Type</option>
                                                <option value="Private Limited">Private Limited</option>
                                                <option value="Proprietorship">Proprietorship</option>
                                                <option value="Partnership">Partnership</option>
                                                <option value="LLP">LLP</option>
                                            </select>
                                        </div>
                                        <div><label style={labelStyle}>GST Number</label>
                                            <input style={inputStyle} type="text" placeholder="22AAAAA0000A1Z5" value={authData.gst_number} onChange={e => setAuthData({...authData, gst_number: e.target.value.toUpperCase()})} />
                                        </div>
                                        <div><label style={labelStyle}>PAN Number</label>
                                            <input style={inputStyle} type="text" placeholder="AAAAA0000A" value={authData.pan_number} onChange={e => setAuthData({...authData, pan_number: e.target.value.toUpperCase()})} />
                                        </div>
                                    </div>
                                    <div style={{marginTop: '12px'}}><label style={labelStyle}>Registered Company Address</label>
                                        <textarea style={{...inputStyle, height: '80px', resize: 'vertical'}} placeholder="Full registered address" value={authData.company_address} onChange={e => setAuthData({...authData, company_address: e.target.value})} />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── Login credentials ── */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                            {isLogin && <div style={{height: '4px'}} />}
                            <input style={inputStyle} type="email" placeholder="Email Address" required value={authData.email} onChange={e => setAuthData({...authData, email: e.target.value})} />
                            <input style={inputStyle} type="password" placeholder="Password" required value={authData.password} onChange={e => setAuthData({...authData, password: e.target.value})} />
                        </div>

                        <button type="submit" style={styles.primaryBtn} disabled={authLoading}>
                            {authLoading ? 'Processing...' : (isLogin ? 'Secure Login' : 'Create Account')}
                        </button>
                    </form>

                    <div style={{marginTop: '20px', textAlign: 'center', color: '#64748b'}}>
                        {isLogin ? "Don't have an account?" : "Already registered?"}
                        <button onClick={() => setIsLogin(!isLogin)} style={{background: 'none', border: 'none', color: 'var(--primary-orange)', fontWeight: 600, marginLeft: '10px', cursor: 'pointer', textDecoration: 'underline'}}>
                            {isLogin ? 'Sign Up' : 'Login'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- Calculate Analytics KPIs ---
    const selectedAdObj = ads.find(a => a.id.toString() === analyticsAd?.toString());
    const cpcRate = selectedAdObj ? parseFloat(selectedAdObj.cpc_rate || 0) : 0;
    const cpmRate = selectedAdObj ? parseFloat(selectedAdObj.cpm_rate || 0) : 0;

    const filteredAnalyticsData = analyticsData.filter(d => {
        if (analyticsFilter === 'all') return true;
        
        const rowDate = new Date(d.date);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        
        if (analyticsFilter === '7') {
            const last7 = new Date();
            last7.setDate(today.getDate() - 7);
            last7.setHours(0, 0, 0, 0);
            return rowDate >= last7 && rowDate <= today;
        }
        
        if (analyticsFilter === '30') {
            const last30 = new Date();
            last30.setDate(today.getDate() - 30);
            last30.setHours(0, 0, 0, 0);
            return rowDate >= last30 && rowDate <= today;
        }
        
        if (analyticsFilter === 'custom' && analyticsCustomDates.start && analyticsCustomDates.end) {
            const start = new Date(analyticsCustomDates.start);
            start.setHours(0, 0, 0, 0);
            const end = new Date(analyticsCustomDates.end);
            end.setHours(23, 59, 59, 999);
            return rowDate >= start && rowDate <= end;
        }
        
        return true;
    }).map(d => {
        const spend = (d.clicks * cpcRate) + ((d.impressions / 1000) * cpmRate);
        const ctr = d.impressions > 0 ? ((d.clicks / d.impressions) * 100).toFixed(2) : '0.00';
        return { ...d, spend, ctr };
    });

    const kpiImpressions = filteredAnalyticsData.reduce((sum, d) => sum + d.impressions, 0);
    const kpiClicks = filteredAnalyticsData.reduce((sum, d) => sum + d.clicks, 0);
    const kpiSpend = filteredAnalyticsData.reduce((sum, d) => sum + d.spend, 0);
    const kpiCtr = kpiImpressions > 0 ? ((kpiClicks / kpiImpressions) * 100).toFixed(2) : '0.00';
    // ---------------------------------

    return (
        <>
            <style>{`
                /* ===== SPONSOR PORTAL — MOBILE RESPONSIVE ===== */

                /* Layout */
                .sp-layout { display: flex; height: 100vh; }
                .sp-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
                .sp-content { padding: 24px; overflow-y: auto; flex: 1; }

                /* Sidebar */
                .sp-sidebar { width: 240px; background: var(--bg-card, #fff); border-right: 1px solid var(--border-color, #e2e8f0); display: flex; flex-direction: column; overflow-y: auto; flex-shrink: 0; transition: width 0.25s ease; }
                .sp-sidebar.collapsed { width: 72px; }
                .sp-sidebar.collapsed .sidebar-group-title,
                .sp-sidebar.collapsed .sa-nav-item span,
                .sp-sidebar.collapsed .sp-sidebar-logout span,
                .sp-sidebar.collapsed .sp-brand-text { display: none !important; }
                .sp-sidebar.collapsed .sa-nav-item { justify-content: center; gap: 0; }
                .sp-sidebar.collapsed .sa-logout-btn { justify-content: center; }

                /* Top Header Bar */
                .sp-header { height: 68px; background: var(--bg-card, #fff); border-bottom: 1px solid var(--border-color, #e2e8f0); display: flex; align-items: center; justify-content: space-between; padding: 0 24px; flex-shrink: 0; }
                .sp-header-hamburger { display: none; background: none; border: none; font-size: 1.4rem; color: #475569; cursor: pointer; padding: 0; margin-right: 12px; }
                .sp-header-actions { display: flex; align-items: center; gap: 12px; }

                /* Stats Grid */
                .sp-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 24px; }

                /* Wallet Grid */
                .sp-wallet-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 24px; }

                /* KYC 2-col grids */
                .sp-form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .sp-form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; }

                /* Table wrappers */
                .sp-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                .sp-table-wrap table { min-width: 500px; }

                /* Modal */
                .sp-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9000; display: flex; align-items: center; justify-content: center; padding: 16px; }
                .sp-modal-box { background: #fff; border-radius: 16px; width: 100%; max-width: 680px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
                .sp-modal-body { padding: 20px; overflow-y: auto; flex: 1; }

                /* Analytics header flex */
                .sp-analytics-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }

                /* Mobile Tables - Hidden on Desktop */
                .mobile-only-th, .mobile-only-td { display: none; }

                /* ===== MOBILE BREAKPOINT ===== */
                @media (max-width: 768px) {
                    .sp-layout { flex-direction: column; height: 100vh; overflow: hidden; }
                    .sp-main { flex: 1; overflow-y: auto; overflow-x: hidden; }
                    .sp-content { padding: 14px; }

                    /* Modal Mobile Fixes (Centered Popup) */
                    .sp-modal-overlay { padding: 24px !important; align-items: center !important; justify-content: center !important; }
                    .sp-modal-box { max-height: 85vh !important; border-radius: 16px !important; width: 100% !important; margin: 0 auto !important; }


                    /* Sidebar Drawer */
                    .sp-sidebar {
                        position: fixed; top: 0; bottom: 0; left: 0; z-index: 1000;
                        height: 100vh; width: 260px !important;
                        transform: translateX(0);
                        box-shadow: 4px 0 24px rgba(0,0,0,0.1);
                        border-right: none;
                    }
                    .sp-sidebar.collapsed {
                        transform: translateX(-100%);
                    }
                    /* Override desktop collapsed styles to keep content visible inside drawer */
                    .sp-sidebar.collapsed .sidebar-group-title,
                    .sp-sidebar.collapsed .sa-nav-item span,
                    .sp-sidebar.collapsed .sp-sidebar-logout span,
                    .sp-sidebar.collapsed .sp-brand-text { display: block !important; }
                    .sp-sidebar.collapsed .sa-nav-item, 
                    .sp-sidebar.collapsed .sa-logout-btn { justify-content: flex-start !important; }
                    .sp-sidebar.collapsed .sa-nav-item { gap: 12px; }

                    /* Header cleanup */
                    .sp-header { padding: 0 14px !important; height: 60px !important; flex-wrap: nowrap !important; gap: 8px !important; }
                    .sp-header-hamburger { display: block; margin-right: 8px; }
                    .sp-header h2 { font-size: 1rem !important; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px; }
                    .sp-create-btn-text { display: none; }
                    .sp-wallet-badge { padding: 4px 8px; font-size: 0.85rem; white-space: nowrap; }
                    .sp-header-actions { flex-wrap: nowrap !important; gap: 8px !important; display: flex; }

                    /* Main content on mobile */
                    .sp-main { order: 1; }

                    /* Mobile Table Optimisation */
                    .hide-on-mobile { display: none !important; }
                    .mobile-only-td { display: block !important; }
                    .sp-table-wrap { overflow-x: visible !important; width: 100%; display: block; padding: 0 !important; border: none !important; box-shadow: none !important; background: transparent !important; }
                    .sp-table-wrap table { min-width: 100% !important; border-collapse: separate; border-spacing: 0 10px; }
                    .sp-table-wrap thead { display: none; }
                    .sp-table-wrap tbody tr { background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.06); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; margin-bottom: 10px; border: 1px solid #f1f5f9; }
                    .sp-table-wrap td { display: block; padding: 0 !important; border: none !important; white-space: normal !important; }
                    .sp-table-wrap td.flex-grow-mobile { flex: 1; padding-right: 15px !important; }

                    /* Grids collapse to 1 col */
                    .sp-stats-grid { grid-template-columns: 1fr !important; gap: 12px; }
                    .sp-wallet-grid { grid-template-columns: 1fr !important; gap: 14px; }
                    .sp-form-grid-2 { grid-template-columns: 1fr !important; gap: 12px; }
                    .sp-form-grid-3 { grid-template-columns: 1fr !important; gap: 12px; }

                    /* Modal fills screen */
                    .sp-modal-overlay { padding: 0; align-items: flex-end; }
                    .sp-modal-box { border-radius: 20px 20px 0 0; max-height: 92vh; }

                    /* Analytics header stacks */
                    .sp-analytics-header { flex-direction: column; align-items: flex-start; }
                    .sp-analytics-header select { width: 100%; }
                }
            `}</style>
            <div style={styles.dashboardLayout} className="sp-layout">
                {/* Mobile overlay */}
                {window.innerWidth <= 768 && sidebarOpen && (
                    <div onClick={() => setSidebarOpen(false)} style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999}} />
                )}
                {/* Sidebar */}
                <aside className={`sa-sidebar sp-sidebar${sidebarOpen ? '' : ' collapsed'}`}>
                    <div style={{ padding: '0 10px', marginBottom: '15px', marginTop: '10px' }} className="sp-sidebar-brand">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <img src="https://menu.searchisend.in/logo512.png" alt="Logo" style={{ width: '22px', height: '22px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }} />
                            <div className="sp-brand-text" style={{ overflow: 'hidden' }}>
                                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem', whiteSpace: 'nowrap' }}>SIE QR Menu</h3>
                                <span style={{ fontSize: '0.72rem', color: profile.kyc_status === 'Verified' ? 'var(--success)' : 'var(--warning)' }}>
                                    <i className="fas fa-shield-alt" style={{ marginRight: '4px' }}></i>
                                    {profile.kyc_status || 'Pending'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <nav style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div className="sidebar-group-title" style={{ marginTop: '10px' }}>MAIN</div>
                        <button className={`sa-nav-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                            <i className="fas fa-home"></i> <span>Dashboard</span>
                        </button>
                        <button className={`sa-nav-item ${activeTab === 'ads' ? 'active' : ''}`} onClick={() => setActiveTab('ads')}>
                            <i className="fas fa-ad"></i> <span>Campaigns</span>
                        </button>
                        <button className={`sa-nav-item ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>
                            <i className="fas fa-chart-pie"></i> <span>Analytics</span>
                        </button>

                        <div className="sidebar-group-title" style={{ marginTop: '10px' }}>FINANCE & COMPLIANCE</div>
                        <button className={`sa-nav-item ${activeTab === 'wallet' ? 'active' : ''}`} onClick={() => setActiveTab('wallet')}>
                            <i className="fas fa-wallet"></i> <span>Wallet</span>
                        </button>
                        <button className={`sa-nav-item ${activeTab === 'kyc' ? 'active' : ''}`} onClick={() => setActiveTab('kyc')}>
                            <i className="fas fa-file-contract"></i> <span>KYC</span>
                        </button>
                    </nav>

                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }} className="sp-sidebar-logout">
                        {/* Hamburger / collapse toggle */}
                        <button
                            onClick={() => setSidebarOpen(o => !o)}
                            className="sa-nav-item"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary, #475569)', width: '100%', justifyContent: sidebarOpen ? 'flex-start' : 'center', marginBottom: '5px' }}
                            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                        >
                            <i className={sidebarOpen ? "fas fa-chevron-left" : "fas fa-chevron-right"} style={{ width: '20px', textAlign: 'center' }}></i>
                            {sidebarOpen && <span>Collapse</span>}
                        </button>
                        <button onClick={handleLogout} className="sa-logout-btn" style={{ width: '100%', justifyContent: sidebarOpen ? 'flex-start' : 'center' }}>
                            <i className="fas fa-sign-out-alt"></i> <span>Logout</span>
                        </button>
                    </div>
                </aside>

            {/* Main Content */}
            <main style={styles.mainContent} className="sp-main">
                <header style={styles.mainHeader} className="sp-header">
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <button className="sp-header-hamburger" onClick={() => setSidebarOpen(true)}>
                            <i className="fas fa-bars"></i>
                        </button>
                        <h2 style={{margin: 0, color: '#1f2937', fontSize: '1.25rem'}}>
                            {activeTab === 'overview' && 'Dashboard Overview'}
                            {activeTab === 'ads' && 'Ad Campaigns'}
                            {activeTab === 'analytics' && 'Campaign Analytics'}
                            {activeTab === 'wallet' && 'Wallet & Billing'}
                            {activeTab === 'kyc' && 'KYC Verification'}
                        </h2>
                    </div>
                    
                    <div className="sp-header-actions">
                        <div style={styles.walletBadge} className="sp-wallet-badge">
                            <i className="fas fa-rupee-sign" style={{marginRight: '5px'}}></i>
                            {parseFloat(walletBalance).toFixed(2)}
                        </div>
                        <button onClick={() => setShowCreateAd(true)} style={styles.createBtn}>
                            <i className="fas fa-plus"></i> <span className="sp-create-btn-text">New Campaign</span>
                        </button>
                    </div>
                </header>

                <div className="sp-content">
                    
                    {/* KYC Warning */}
                    {profile.kyc_status !== 'Verified' && activeTab !== 'kyc' && (
                        <div style={styles.kycWarning}>
                            <i className="fas fa-exclamation-triangle"></i>
                            <div>
                                <strong>Action Required:</strong> Your account KYC is {profile.kyc_status || 'Pending'}. 
                                You cannot create campaigns until your identity is verified.
                                <span style={{marginLeft: '10px', textDecoration: 'underline', cursor: 'pointer'}} onClick={() => setActiveTab('kyc')}>Complete KYC Now</span>
                            </div>
                        </div>
                    )}

                    {/* OVERVIEW TAB */}
                    {activeTab === 'overview' && (
                        <div>
                            <div className="sp-stats-grid">
                                <div style={styles.statCard}>
                                    <div style={styles.statIcon}><i className="fas fa-ad"></i></div>
                                    <div style={styles.statInfo}>
                                        <h4>Total Campaigns</h4>
                                        <h2>{ads.length}</h2>
                                    </div>
                                </div>
                                <div style={styles.statCard}>
                                    <div style={styles.statIcon}><i className="fas fa-eye"></i></div>
                                    <div style={styles.statInfo}>
                                        <h4>Total Impressions</h4>
                                        <h2>{ads.reduce((a, b) => a + parseInt(b.total_impressions || 0), 0)}</h2>
                                    </div>
                                </div>
                                <div style={styles.statCard}>
                                    <div style={styles.statIcon}><i className="fas fa-mouse-pointer"></i></div>
                                    <div style={styles.statInfo}>
                                        <h4>Total Clicks</h4>
                                        <h2>{ads.reduce((a, b) => a + parseInt(b.total_clicks || 0), 0)}</h2>
                                    </div>
                                </div>
                            </div>
                            
                            <h3 style={{marginTop: '24px', marginBottom: '12px', color: '#1f2937'}}>Recent Campaigns</h3>
                            {ads.length === 0 ? (
                                <div style={{padding: '30px', textAlign: 'center', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b'}}>
                                    <i className="fas fa-box-open fa-2x" style={{marginBottom: '10px', color: '#94a3b8'}}></i>
                                    <p style={{margin: 0}}>No recent campaigns found. Create your first campaign to see it here!</p>
                                </div>
                            ) : (
                                <div style={styles.card} className="sp-table-wrap">
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Campaign</th>
                                                <th className="hide-on-mobile">Targeting</th>
                                                <th className="hide-on-mobile">Status</th>
                                                <th className="hide-on-mobile">Performance</th>
                                                <th className="mobile-only-th">Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ads.slice(0, 5).map(ad => (
                                                <tr key={ad.id}>
                                                    <td className="flex-grow-mobile">
                                                        <div style={{fontWeight: 600}}>{ad.title}</div>
                                                        <div className="hide-on-mobile">
                                                            <a href={ad.target_url} target="_blank" rel="noreferrer" style={{fontSize: '0.8rem', color: '#3b82f6'}}>{ad.target_url}</a>
                                                        </div>
                                                        <div className="mobile-only-td" style={{marginTop: '4px'}}>
                                                             <span style={styles.statusBadge(ad.status)}>{ad.status}</span>
                                                        </div>
                                                    </td>
                                                    <td className="hide-on-mobile">
                                                        {(() => {
                                                            const sList = ad.state_name ? ad.state_name.split(', ') : [];
                                                            if (sList.length === 0) return 'Global';
                                                            if (sList.length === 1) return ad.city_name ? `${ad.city_name}, ${sList[0]}` : sList[0];
                                                            return `Multiple Regions (${sList.length})`;
                                                        })()}
                                                    </td>
                                                    <td className="hide-on-mobile"><span style={styles.statusBadge(ad.status)}>{ad.status}</span></td>
                                                    <td className="hide-on-mobile">{ad.total_impressions} views / {ad.total_clicks} clicks</td>
                                                    <td className="mobile-only-td">
                                                        <button onClick={() => setSelectedAdForDetails(ad)} style={{background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 12px', color: '#475569', cursor: 'pointer'}}>
                                                            <i className="fas fa-eye"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ADS TAB */}
                    {activeTab === 'ads' && (
                        ads.length === 0 ? (
                            <div style={{padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b'}}>
                                <i className="fas fa-ad fa-3x" style={{marginBottom: '15px', color: '#94a3b8'}}></i>
                                <h3 style={{margin: '0 0 10px 0', color: '#334155'}}>No Campaigns Yet</h3>
                                <p style={{margin: '0 0 20px 0'}}>You haven't created any campaigns yet. Start reaching your audience today!</p>
                                <button onClick={() => setShowCreateAd(true)} style={{...styles.primaryBtn, maxWidth: '200px', margin: '0 auto'}}>Create New Campaign</button>
                            </div>
                        ) : (
                            <div>
                                <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '15px'}}>
                                    <button onClick={() => setShowCreateAd(true)} style={styles.primaryBtn}>
                                        <i className="fas fa-plus"></i> Create New Campaign
                                    </button>
                                </div>
                                <div style={styles.card} className="sp-table-wrap">
                                    <table style={styles.table}>
                                        <thead>
                                        <tr>
                                            <th className="hide-on-mobile">Banner</th>
                                            <th>Campaign Details</th>
                                            <th className="hide-on-mobile">Status</th>
                                            <th className="hide-on-mobile">Pricing</th>
                                            <th className="hide-on-mobile">Performance</th>
                                            <th className="mobile-only-th">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ads.map(ad => (
                                            <tr key={ad.id}>
                                                <td className="hide-on-mobile">
                                                    <img src={ad.image_url} alt="Ad" style={{width: '80px', height: '50px', objectFit: 'cover', borderRadius: '4px'}} />
                                                </td>
                                                <td className="flex-grow-mobile">
                                                    <div style={{fontWeight: 600}}>{ad.title}</div>
                                                    <div style={{fontSize: '0.8rem', color: '#6b7280'}}>{ad.placement_zone.replace('_', ' ')}</div>
                                                    <div className="mobile-only-td" style={{marginTop: '4px'}}>
                                                         <span style={styles.statusBadge(ad.status)}>{ad.status}</span>
                                                    </div>
                                                </td>
                                                <td className="hide-on-mobile"><span style={styles.statusBadge(ad.status)}>{ad.status}</span></td>
                                                <td className="hide-on-mobile"><div style={{fontSize: '0.9rem'}}>CPC: ₹{ad.cpc_rate}</div><div style={{fontSize: '0.9rem'}}>CPM: ₹{ad.cpm_rate || 0}</div></td>
                                                <td className="hide-on-mobile">
                                                    <div style={{fontSize: '0.9rem'}}><i className="fas fa-eye text-gray-400"></i> {ad.total_impressions}</div>
                                                    <div style={{fontSize: '0.9rem'}}><i className="fas fa-mouse-pointer text-gray-400"></i> {ad.total_clicks}</div>
                                                </td>
                                                <td className="mobile-only-td">
                                                    <button onClick={() => setSelectedAdForDetails(ad)} style={{background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 12px', color: '#475569', cursor: 'pointer'}}>
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </div>
                        )
                    )}

                    {/* KYC TAB */}
                    {activeTab === 'kyc' && (
                        <div style={{maxWidth: '800px'}}>
                            <div style={styles.card}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap'}}>
                                    <div style={{width: '60px', height: '60px', borderRadius: '50%', background: profile.kyc_status === 'Verified' ? '#dcfce7' : '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: profile.kyc_status === 'Verified' ? '#16a34a' : '#d97706'}}>
                                        <i className="fas fa-building"></i>
                                    </div>
                                    <div>
                                        <h3 style={{margin: 0}}>Business Verification</h3>
                                        <p style={{margin: '5px 0 0', color: '#6b7280', fontSize: '0.9rem'}}>Status: <strong style={{color: profile.kyc_status === 'Verified' ? '#16a34a' : '#d97706'}}>{profile.kyc_status || 'Unsubmitted'}</strong></p>
                                    </div>
                                </div>

                                <form onSubmit={handleKycSubmit} style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                                    
                                    <h4 style={{margin: '0', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px'}}>General Profile</h4>
                                    <div className="sp-form-grid-2">
                                        <div>
                                            <label style={styles.formLabel}>Contact Person Name *</label>
                                            <input type="text" style={styles.formInput} required value={kycForm.contact_name} onChange={e => setKycForm({...kycForm, contact_name: e.target.value})} disabled={profile.kyc_status === 'Verified'} />
                                        </div>
                                        <div>
                                            <label style={styles.formLabel}>Business / Company Name *</label>
                                            <input type="text" style={styles.formInput} required value={kycForm.company_name} onChange={e => setKycForm({...kycForm, company_name: e.target.value})} disabled={profile.kyc_status === 'Verified'} />
                                        </div>
                                    </div>
                                    <div className="sp-form-grid-2">
                                        <div>
                                            <label style={styles.formLabel}>Personal Phone Number *</label>
                                            <input type="tel" style={styles.formInput} required value={kycForm.phone} onChange={e => setKycForm({...kycForm, phone: e.target.value})} disabled={profile.kyc_status === 'Verified'} />
                                        </div>
                                        <div>
                                            <label style={styles.formLabel}>Business Phone Number</label>
                                            <input type="tel" style={styles.formInput} value={kycForm.business_phone} onChange={e => setKycForm({...kycForm, business_phone: e.target.value})} disabled={profile.kyc_status === 'Verified'} />
                                        </div>
                                    </div>

                                    <h4 style={{margin: '10px 0 0 0', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px'}}>Legal & Registration Details</h4>
                                    <div className="sp-form-grid-2">
                                        <div>
                                            <label style={styles.formLabel}>GST Number *</label>
                                            <input type="text" style={styles.formInput} required value={kycForm.gst_number} onChange={e => setKycForm({...kycForm, gst_number: e.target.value})} disabled={profile.kyc_status === 'Verified'} />
                                        </div>
                                        <div>
                                            <label style={styles.formLabel}>PAN Number *</label>
                                            <input type="text" style={styles.formInput} required value={kycForm.pan_number} onChange={e => setKycForm({...kycForm, pan_number: e.target.value})} disabled={profile.kyc_status === 'Verified'} />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={styles.formLabel}>Business Type *</label>
                                        <select style={styles.formInput} required value={kycForm.business_type} onChange={e => setKycForm({...kycForm, business_type: e.target.value})} disabled={profile.kyc_status === 'Verified'}>
                                            <option value="">Select Type</option>
                                            <option value="Private Limited">Private Limited</option>
                                            <option value="Proprietorship">Proprietorship</option>
                                            <option value="Partnership">Partnership</option>
                                            <option value="LLP">LLP</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={styles.formLabel}>Registered Company Address *</label>
                                        <textarea style={{...styles.formInput, height: '100px'}} required value={kycForm.company_address} onChange={e => setKycForm({...kycForm, company_address: e.target.value})} disabled={profile.kyc_status === 'Verified'}></textarea>
                                    </div>
                                    {profile.kyc_status !== 'Verified' && (
                                        <button type="submit" style={{...styles.createBtn, alignSelf: 'flex-start'}} disabled={kycLoading}>
                                            {kycLoading ? 'Submitting...' : 'Submit for Verification'}
                                        </button>
                                    )}
                                </form>
                            </div>
                        </div>
                    )}

                    {/* WALLET TAB */}
                    {activeTab === 'wallet' && (
                        <div className="sp-wallet-grid">
                            <div style={styles.card}>
                                <h3>Wallet Balance</h3>
                                <div style={{fontSize: '3rem', fontWeight: 800, color: '#10b981', margin: '20px 0'}}>
                                    ₹{parseFloat(walletBalance).toFixed(2)}
                                </div>
                                <p style={{color: '#6b7280', fontSize: '0.9rem', lineHeight: 1.5}}>
                                    Your wallet balance is used to pay for Ad clicks and impressions. 
                                    Currently, funds must be manually added by the administration team.
                                </p>
                                <div style={{marginTop: '20px', padding: '15px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px'}}>
                                    <h4 style={{color: '#991b1b', margin: '0 0 5px 0'}}><i className="fas fa-info-circle"></i> Tax Disclaimer</h4>
                                    <p style={{margin: 0, fontSize: '0.8rem', color: '#b91c1c'}}>Please note that 18% GST is applicable on all recharges and is non-refundable under any circumstances.</p>
                                </div>
                            </div>
                            <div style={styles.card} className="sp-table-wrap">
                                <h3>Transaction History</h3>
                                {transLoading ? <p>Loading...</p> : (
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th className="hide-on-mobile">Type</th>
                                                <th className="hide-on-mobile">Date</th>
                                                <th style={{textAlign: 'right'}}>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.length === 0 ? <tr><td colSpan="4">No transactions found</td></tr> : 
                                                transactions.map(t => (
                                                    <tr key={t.id}>
                                                        <td className="flex-grow-mobile">
                                                            <div style={{fontWeight: 600}}>{t.description}</div>
                                                            <div style={{fontSize: '0.8rem', color: '#6b7280'}}>{new Date(t.created_at).toLocaleDateString()}</div>
                                                        </td>
                                                        <td className="hide-on-mobile"><span style={{color: t.type === 'Credit' ? '#10b981' : '#ef4444', fontWeight: 600}}>{t.type}</span></td>
                                                        <td className="hide-on-mobile">{new Date(t.created_at).toLocaleDateString()}</td>
                                                        <td style={{textAlign: 'right'}}>
                                                            <div style={{fontWeight: 600, fontSize: '1.1rem'}}>₹{t.amount}</div>
                                                            <div className="mobile-only-td" style={{fontSize: '0.8rem'}}>
                                                                <span style={{color: t.type === 'Credit' ? '#10b981' : '#ef4444', fontWeight: 600}}>{t.type}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ANALYTICS TAB */}
                    {activeTab === 'analytics' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* Controls Card */}
                            <div style={{ ...styles.card, padding: '20px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ flex: '1 1 300px' }}>
                                        <label style={{ ...styles.formLabel, margin: '0 0 5px 0' }}>Select Campaign</label>
                                        <select style={styles.formInput} value={analyticsAd} onChange={e => setAnalyticsAd(e.target.value)}>
                                            {ads.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                                        </select>
                                    </div>
                                    <div style={{ flex: '1 1 300px' }}>
                                        <label style={{ ...styles.formLabel, margin: '0 0 5px 0' }}>Date Range</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <select style={{ ...styles.formInput, flex: 1 }} value={analyticsFilter} onChange={e => setAnalyticsFilter(e.target.value)}>
                                                <option value="7">Last 7 Days</option>
                                                <option value="30">Last 30 Days</option>
                                                <option value="all">All Time</option>
                                                <option value="custom">Custom Date Range</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                
                                {analyticsFilter === 'custom' && (
                                    <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ ...styles.formLabel, margin: '0 0 5px 0' }}>Start Date</label>
                                            <input type="date" style={styles.formInput} value={analyticsCustomDates.start} onChange={e => setAnalyticsCustomDates({...analyticsCustomDates, start: e.target.value})} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ ...styles.formLabel, margin: '0 0 5px 0' }}>End Date</label>
                                            <input type="date" style={styles.formInput} value={analyticsCustomDates.end} onChange={e => setAnalyticsCustomDates({...analyticsCustomDates, end: e.target.value})} />
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {analyticsLoading ? <div style={{...styles.card, textAlign: 'center', padding: '40px'}}><p>Loading analytics data...</p></div> : 
                                filteredAnalyticsData.length > 0 ? (
                                <>
                                    {/* KPI Cards */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                                        <div style={{ ...styles.card, padding: '20px', textAlign: 'center' }}>
                                            <div style={{ color: '#6b7280', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase' }}>Impressions</div>
                                            <div style={{ color: '#F97316', fontSize: '2rem', fontWeight: 800, margin: '10px 0' }}>{kpiImpressions.toLocaleString()}</div>
                                        </div>
                                        <div style={{ ...styles.card, padding: '20px', textAlign: 'center' }}>
                                            <div style={{ color: '#6b7280', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase' }}>Clicks</div>
                                            <div style={{ color: '#22C55E', fontSize: '2rem', fontWeight: 800, margin: '10px 0' }}>{kpiClicks.toLocaleString()}</div>
                                        </div>
                                        <div style={{ ...styles.card, padding: '20px', textAlign: 'center' }}>
                                            <div style={{ color: '#6b7280', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase' }}>Avg. CTR</div>
                                            <div style={{ color: '#3B82F6', fontSize: '2rem', fontWeight: 800, margin: '10px 0' }}>{kpiCtr}%</div>
                                        </div>
                                        <div style={{ ...styles.card, padding: '20px', textAlign: 'center' }}>
                                            <div style={{ color: '#6b7280', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase' }}>Total Spend</div>
                                            <div style={{ color: '#EF4444', fontSize: '2rem', fontWeight: 800, margin: '10px 0' }}>₹{kpiSpend.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                        </div>
                                    </div>

                                    {/* Graph */}
                                    <div style={styles.card}>
                                        <h3 style={{ margin: '0 0 20px 0' }}>Performance Chart</h3>
                                        <div style={{ height: '400px', width: '100%' }}>
                                            <Line 
                                                data={{
                                                    labels: filteredAnalyticsData.map(d => d.date),
                                                    datasets: [
                                                        {
                                                            label: 'Impressions',
                                                            data: filteredAnalyticsData.map(d => d.impressions),
                                                            borderColor: '#F97316',
                                                            backgroundColor: 'rgba(249, 115, 22, 0.1)',
                                                            tension: 0.4,
                                                            yAxisID: 'y'
                                                        },
                                                        {
                                                            label: 'Clicks',
                                                            data: filteredAnalyticsData.map(d => d.clicks),
                                                            borderColor: '#22C55E',
                                                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                                            tension: 0.4,
                                                            yAxisID: 'y1'
                                                        },
                                                        {
                                                            label: 'Spend (₹)',
                                                            data: filteredAnalyticsData.map(d => d.spend),
                                                            borderColor: '#EF4444',
                                                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                            tension: 0.4,
                                                            borderDash: [5, 5],
                                                            yAxisID: 'y2'
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    interaction: { mode: 'index', intersect: false },
                                                    plugins: {
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function(context) {
                                                                    let label = context.dataset.label || '';
                                                                    if (label) { label += ': '; }
                                                                    if (context.dataset.yAxisID === 'y2') {
                                                                        label += '₹' + parseFloat(context.raw).toFixed(2);
                                                                    } else {
                                                                        label += context.raw;
                                                                    }
                                                                    return label;
                                                                }
                                                            }
                                                        }
                                                    },
                                                    scales: {
                                                        y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Impressions' } },
                                                        y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Clicks' } },
                                                        y2: { type: 'linear', display: false }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Data Table */}
                                    <div style={styles.card} className="sp-table-wrap">
                                        <h3 style={{ margin: '0 0 20px 0' }}>Daily Breakdown</h3>
                                        <table style={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th style={{textAlign: 'right'}}>Impressions</th>
                                                    <th style={{textAlign: 'right'}}>Clicks</th>
                                                    <th style={{textAlign: 'right'}}>CTR</th>
                                                    <th style={{textAlign: 'right'}}>Spend</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredAnalyticsData.slice().reverse().map((d, index) => (
                                                    <tr key={index}>
                                                        <td>{new Date(d.date).toLocaleDateString()}</td>
                                                        <td style={{textAlign: 'right'}}>{d.impressions.toLocaleString()}</td>
                                                        <td style={{textAlign: 'right', color: '#22C55E', fontWeight: 600}}>{d.clicks.toLocaleString()}</td>
                                                        <td style={{textAlign: 'right'}}>{d.ctr}%</td>
                                                        <td style={{textAlign: 'right', color: '#EF4444', fontWeight: 600}}>₹{d.spend.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div style={{...styles.card, padding: '50px', textAlign: 'center', color: '#9ca3af'}}>
                                    <i className="fas fa-chart-line fa-3x" style={{marginBottom: '15px'}}></i>
                                    <p>No analytics data available for the selected range.</p>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </main>

            {/* Ad Details Modal */}
            {selectedAdForDetails && (
                <div style={styles.modalOverlay} className="sp-modal-overlay">
                    <div style={styles.modalContent} className="sp-modal-box">
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0}}>
                            <h2 style={{margin: 0, fontSize: '1.1rem', color: '#1f2937'}}>Campaign Details</h2>
                            <button onClick={() => setSelectedAdForDetails(null)} style={{background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280'}}>×</button>
                        </div>
                        <div className="sp-modal-body" style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                            <div><strong>Title:</strong> <span style={{marginLeft: '5px'}}>{selectedAdForDetails.title}</span></div>
                            <div><strong>Status:</strong> <span style={{marginLeft: '5px', ...styles.statusBadge(selectedAdForDetails.status)}}>{selectedAdForDetails.status}</span></div>
                            <div><strong>Targeting:</strong> <span style={{marginLeft: '5px'}}>{selectedAdForDetails.state_name || 'Global'}</span></div>
                            <div><strong>Placement:</strong> <span style={{marginLeft: '5px'}}>{selectedAdForDetails.placement_zone?.replace('_', ' ')}</span></div>
                            {selectedAdForDetails.image_url && <div><strong>Banner:</strong><br/><img src={selectedAdForDetails.image_url} alt="Ad" style={{width: '100%', maxWidth: '200px', marginTop: '10px', borderRadius: '8px', objectFit: 'cover'}}/></div>}
                            <hr style={{border: 'none', borderTop: '1px solid #e5e7eb', margin: '10px 0'}}/>
                            <div><strong>CPC:</strong> <span style={{marginLeft: '5px'}}>₹{selectedAdForDetails.cpc_rate}</span></div>
                            <div><strong>CPM:</strong> <span style={{marginLeft: '5px'}}>₹{selectedAdForDetails.cpm_rate || 0}</span></div>
                            <div><strong>Impressions:</strong> <span style={{marginLeft: '5px'}}>{selectedAdForDetails.total_impressions}</span></div>
                            <div><strong>Clicks:</strong> <span style={{marginLeft: '5px'}}>{selectedAdForDetails.total_clicks}</span></div>
                            <div><strong>Target URL:</strong><br/><a href={selectedAdForDetails.target_url} target="_blank" rel="noreferrer" style={{color: '#3b82f6', wordBreak: 'break-all'}}>{selectedAdForDetails.target_url}</a></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Ad Wizard Modal */}
            {showCreateAd && (
                <div style={styles.modalOverlay} className="sp-modal-overlay">
                    <div style={styles.modalContent} className="sp-modal-box">
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0}}>
                            <h2 style={{margin: 0, fontSize: '1.1rem', color: '#1f2937'}}>Create Campaign Wizard</h2>
                            <button onClick={() => setShowCreateAd(false)} style={{background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280'}}>×</button>
                        </div>
                        <div className="sp-modal-body" style={{overflowY: 'auto'}}>
                            <form onSubmit={handleCreateAd} style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                                <div>
                                    <label style={styles.formLabel}>Campaign Title *</label>
                                    <input type="text" style={styles.formInput} required value={adForm.title} onChange={e => setAdForm({...adForm, title: e.target.value})} />
                                </div>
                                <div>
                                    <label style={styles.formLabel}>Target URL *</label>
                                    <input type="url" style={styles.formInput} required value={adForm.target_url} onChange={e => setAdForm({...adForm, target_url: e.target.value})} />
                                </div>
                                <div className="sp-form-grid-2">
                                    <div>
                                        <label style={styles.formLabel}>Main Category *</label>
                                        <select style={styles.formInput} required value={adForm.main_category_id} onChange={e => setAdForm({...adForm, main_category_id: e.target.value})}>
                                            <option value="">Select Category</option>
                                            {categories.filter(c => !c.parent_id || c.parent_id === '0').map(c => <option key={c.id} value={c.id}>{c.category_name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={styles.formLabel}>Placement Zone</label>
                                        <select style={styles.formInput} value={adForm.placement_zone} onChange={e => setAdForm({...adForm, placement_zone: e.target.value})}>
                                            <option value="in_menu_first">In-Menu (First Slot)</option>
                                            <option value="in_menu_second">In-Menu (Second Slot)</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label style={styles.formLabel}>Ad Image (Banner) *</label>
                                    <input type="file" style={styles.formInput} required accept="image/*" onChange={e => setAdForm({...adForm, image: e.target.files[0]})} />
                                </div>
                                <div style={{marginTop: '20px', padding: '15px', background: 'var(--card-bg-color)', borderRadius: '8px', border: '1px solid var(--border-color)'}}>
                                    <h4 style={{margin: '0 0 10px 0', color: 'var(--text-color-headings)'}}><i className="fas fa-bullseye"></i> Targeting & Billing</h4>
                                    
                                    <div className="sp-form-grid-3">
                                        <div>
                                            <label style={styles.formLabel}>Target States (Multi-select)</label>
                                            <select multiple style={{...styles.formInput, height: '100px'}} value={adForm.target_state_ids} onChange={e => setAdForm({...adForm, target_state_ids: Array.from(e.target.selectedOptions, option => option.value), target_city_ids: []})}>
                                                {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={styles.formLabel}>Target Cities (Multi-select)</label>
                                            <select multiple style={{...styles.formInput, height: '100px'}} value={adForm.target_city_ids} onChange={e => setAdForm({...adForm, target_city_ids: Array.from(e.target.selectedOptions, option => option.value)})} disabled={!adForm.target_state_ids || adForm.target_state_ids.length === 0}>
                                                {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={styles.formLabel}>Target Pincode</label>
                                            <input type="text" style={styles.formInput} placeholder="e.g. 400001" value={adForm.target_pincode} onChange={e => setAdForm({...adForm, target_pincode: e.target.value})} />
                                        </div>
                                    </div>

                                    <p style={{fontSize: '0.85rem', color: 'var(--text-color-secondary)', margin: '0 0 10px 0'}}>Once submitted, Super Admin will review and set the final CPC/CPM pricing based on your placement choice.</p>
                                </div>
                                <button type="submit" style={styles.primaryBtn}>Submit Campaign for Review</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </>
    );
};

const styles = {
    authContainer: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-color)',
        fontFamily: 'var(--font-family-main)'
    },
    glassCard: {
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        padding: '40px',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
    },
    logoContainer: {
        textAlign: 'center',
        marginBottom: '30px'
    },
    logoIcon: {
        width: '60px',
        height: '60px',
        background: 'var(--primary-orange)',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2rem',
        color: 'var(--text-color-on-dark-bg)',
        fontWeight: 'bold',
        margin: '0 auto 15px'
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        color: '#1e293b',
        fontSize: '1rem',
        outline: 'none',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box'
    },
    primaryBtn: {
        width: '100%',
        padding: '14px',
        background: 'var(--primary-orange)',
        border: 'none',
        borderRadius: '12px',
        color: 'var(--text-color-on-dark-bg)',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: 'var(--box-shadow-current)',
        transition: 'all 0.3s ease'
    },
    dashboardLayout: {
        display: 'flex',
        height: '100vh',
        background: 'var(--bg-color)',
        fontFamily: 'var(--font-family-main)'
    },
    mainContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    mainHeader: {
        height: '80px',
        background: 'var(--card-bg-color)',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 30px'
    },
    walletBadge: {
        background: 'var(--light-orange)',
        color: 'var(--primary-orange)',
        padding: '8px 16px',
        borderRadius: '20px',
        fontWeight: 700,
        border: '1px solid var(--primary-orange)',
        display: 'flex',
        alignItems: 'center'
    },
    createBtn: {
        background: 'var(--primary-orange)',
        color: 'var(--text-color-on-dark-bg)',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '8px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: 'var(--box-shadow-current)'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px'
    },
    statCard: {
        background: 'var(--card-bg-color)',
        padding: '25px',
        borderRadius: '16px',
        boxShadow: 'var(--box-shadow-light)',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        border: '1px solid var(--border-color)'
    },
    statIcon: {
        width: '60px',
        height: '60px',
        borderRadius: '12px',
        background: 'var(--light-orange)',
        color: 'var(--primary-orange)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem'
    },
    statInfo: {
        h4: { margin: 0, color: 'var(--text-color-secondary)', fontSize: '0.9rem', fontWeight: 500 },
        h2: { margin: '5px 0 0', color: 'var(--text-color-headings)', fontSize: '1.8rem', fontWeight: 700 }
    },
    card: {
        background: 'var(--card-bg-color)',
        borderRadius: '16px',
        padding: '25px',
        boxShadow: 'var(--box-shadow-light)',
        border: '1px solid var(--border-color)'
    },
    kycWarning: {
        background: '#fffbeb',
        border: '1px solid #fde68a',
        padding: '15px 20px',
        borderRadius: '12px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        color: '#92400e'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        textAlign: 'left'
    },
    statusBadge: (status) => ({
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: status === 'Active' ? '#dcfce7' : status === 'Pending_Approval' ? '#fef3c7' : '#fee2e2',
        color: status === 'Active' ? '#16a34a' : status === 'Pending_Approval' ? '#d97706' : '#dc2626'
    }),
    formLabel: {
        display: 'block',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: 'var(--text-color-headings)',
        marginBottom: '8px'
    },
    formInput: {
        width: '100%',
        padding: '12px 15px',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        outline: 'none',
        fontSize: '1rem',
        color: 'var(--text-color)',
        background: 'var(--bg-color)'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        backdropFilter: 'blur(4px)'
    },
    modalContent: {
        background: 'var(--card-bg-color)',
        width: '100%',
        maxWidth: '600px',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: 'var(--box-shadow-dark)'
    }
};

export default SponsorPortal;
